import React from 'react';
import { TravelProfile } from '../../types/matching';
import { MatchingCard } from './MatchingCard';
import { Users, Shield, ArrowRight, Check } from 'lucide-react';

interface MatchingListProps {
  profiles: TravelProfile[];
  onLike: (profile: TravelProfile) => void;
  onPass: (profile: TravelProfile) => void;
  onMessage?: (profile: TravelProfile) => void;
  isLoading?: boolean;
  error?: string | null;
  currentUserId?: string;
  currentUsername?: string;
  onOpenProfile?: (profileId: string) => void;
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl bg-white dark:bg-gray-800/90 border border-gray-200/60 dark:border-gray-700/50 overflow-hidden">
      <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-700 animate-pulse" />
      <div className="p-4 space-y-3">
        <div className="flex gap-2">
          <div className="h-5 w-16 bg-gray-100 dark:bg-gray-700 rounded-md animate-pulse" />
          <div className="h-5 w-20 bg-gray-100 dark:bg-gray-700 rounded-md animate-pulse" />
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded w-full animate-pulse" />
          <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded w-3/4 animate-pulse" />
        </div>
        <div className="pt-3 border-t border-gray-100 dark:border-gray-700/50">
          <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded w-1/3 animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function PromoCard() {
  return (
    <div className="relative flex flex-col rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 overflow-hidden border border-slate-700/50">
      <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-3xl" />

      <div className="relative flex flex-col flex-1 p-6 gap-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
            <Shield className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Globe PVT</h3>
            <p className="text-xs text-slate-400">Assurance recommandee</p>
          </div>
        </div>

        <ul className="space-y-2.5">
          {[
            'Frais medicaux couverts a 100%',
            'Assistance rapatriement 24h/24',
            'Remboursement mois non utilises',
          ].map((item) => (
            <li key={item} className="flex items-start gap-2.5">
              <div className="w-4 h-4 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-2.5 h-2.5 text-emerald-400" />
              </div>
              <span className="text-sm text-slate-300 leading-snug">{item}</span>
            </li>
          ))}
        </ul>

        <a
          href="/assurance"
          className="mt-auto inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors text-sm font-semibold"
        >
          En savoir plus
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}

export function MatchingList({
  profiles,
  onLike,
  onPass,
  onMessage,
  isLoading,
  error,
  currentUserId,
  currentUsername,
  onOpenProfile
}: MatchingListProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {[...Array(6)].map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl bg-red-50 dark:bg-red-950/30 border border-red-200/50 dark:border-red-800/30 p-8 text-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <Users className="w-7 h-7 text-red-500 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-red-900 dark:text-red-300 mb-1">
              Une erreur est survenue
            </h3>
            <p className="text-sm text-red-600 dark:text-red-400 max-w-md mx-auto">
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (profiles.length === 0) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-800/90 border border-gray-200/60 dark:border-gray-700/50 p-10 text-center">
        <div className="flex flex-col items-center gap-5">
          <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            <Users className="w-7 h-7 text-gray-400 dark:text-gray-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Aucun profil pour le moment
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
              Modifiez vos criteres de recherche ou revenez plus tard pour decouvrir de nouveaux profils.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const ownProfiles = profiles.filter(p => p.isOwnProfile);
  const otherProfiles = profiles.filter(p => !p.isOwnProfile);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 animate-fade-in">
      {ownProfiles.map((profile) => (
        <MatchingCard
          key={profile.id}
          profile={profile}
          onLike={() => onLike(profile)}
          onPass={() => onPass(profile)}
          onMessage={onMessage ? () => onMessage(profile) : undefined}
          similarity={75}
          isOwnProfile={true}
          currentUserId={currentUserId}
          onOpenProfile={onOpenProfile}
        />
      ))}

      {otherProfiles.map((profile, index) => (
        <React.Fragment key={profile.id}>
          <MatchingCard
            profile={profile}
            onLike={() => onLike(profile)}
            onPass={() => onPass(profile)}
            onMessage={onMessage ? () => onMessage(profile) : undefined}
            similarity={75}
            isOwnProfile={profile.isOwnProfile}
            currentUserId={currentUserId}
            onOpenProfile={onOpenProfile}
          />
          {index === 3 && <PromoCard />}
        </React.Fragment>
      ))}
    </div>
  );
}
